import React, { useEffect, useState } from 'react';
import { adminFetch } from '../../lib/api';
import { Megaphone, Plus, X, Edit, Users, Calendar, Target } from 'lucide-react';

interface Campaign {
  id: string;
  name: string;
  description: string;
  target_segment: string;
  channel: string;
  status: string;
  start_date: string;
  end_date: string;
  goal: string;
  email_template_slug: string;
  created_by: string;
  lead_count?: number;
  created_at: string;
}

const CHANNEL_MAP: Record<string, string> = {
  email: '✉️ E-Mail', phone: '📞 Telefon', linkedin: '💼 LinkedIn', event: '📅 Event',
  partner: '🤝 Partner', multi_channel: '📡 Multi-Channel', other: '📋 Sonstiges',
};

const STATUS_MAP: Record<string, { label: string; color: string }> = {
  draft: { label: 'Entwurf', color: '#6b7280' },
  active: { label: 'Aktiv', color: '#10b981' },
  paused: { label: 'Pausiert', color: '#f59e0b' },
  completed: { label: 'Abgeschlossen', color: '#3b82f6' },
  archived: { label: 'Archiviert', color: '#6b7280' },
};

const EMPTY_CAMPAIGN = {
  name: '', description: '', target_segment: '', channel: 'email', status: 'draft',
  start_date: '', end_date: '', goal: '', email_template_slug: '',
};

export const SalesCampaignsPage: React.FC = () => {
  const [campaigns, setCampaigns] = useState<Campaign[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState<Campaign | null>(null);
  const [form, setForm] = useState({ ...EMPTY_CAMPAIGN });
  const [saving, setSaving] = useState(false);

  useEffect(() => { loadCampaigns(); }, []);

  const loadCampaigns = async () => {
    try {
      const res = await adminFetch('/api/admin/sales/campaigns');
      const data = await res.json();
      setCampaigns(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error(error);
      setCampaigns([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const method = editing ? 'PUT' : 'POST';
      const url = editing ? `/api/admin/sales/campaigns/${editing.id}` : '/api/admin/sales/campaigns';
      await adminFetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      setShowForm(false);
      setEditing(null);
      setForm({ ...EMPTY_CAMPAIGN });
      loadCampaigns();
    } catch (error) {
      console.error(error);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page-container">
      <div className="page-header">
        <h1><Megaphone style={{ marginRight: 8, verticalAlign: 'middle' }} size={24} /> Kampagnen</h1>
        <button className="btn btn-primary" onClick={() => { setForm({ ...EMPTY_CAMPAIGN }); setEditing(null); setShowForm(true); }}>
          <Plus size={14} /> Neue Kampagne
        </button>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(340px, 1fr))', gap: 16 }}>
        {loading ? (
          Array.from({ length: 3 }).map((_, i) => <div key={i} className="card skeleton" style={{ height: 160 }} />)
        ) : campaigns.length === 0 ? (
          <div className="card" style={{ padding: 40, textAlign: 'center', color: 'var(--text-secondary)', gridColumn: '1/-1' }}>
            Keine Kampagnen vorhanden
          </div>
        ) : (
          campaigns.map(c => (
            <div key={c.id} className="card" style={{ padding: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <h3 style={{ margin: 0, fontSize: 16 }}>{c.name}</h3>
                  <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
                    <span style={{
                      fontSize: 11, padding: '2px 8px', borderRadius: 8, fontWeight: 600,
                      color: '#fff', background: STATUS_MAP[c.status]?.color || '#666',
                    }}>{STATUS_MAP[c.status]?.label || c.status}</span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {CHANNEL_MAP[c.channel] || c.channel}
                    </span>
                  </div>
                </div>
                <button className="btn btn-ghost btn-sm" onClick={() => {
                  setEditing(c);
                  setForm({
                    name: c.name, description: c.description || '', target_segment: c.target_segment || '',
                    channel: c.channel, status: c.status, start_date: c.start_date || '',
                    end_date: c.end_date || '', goal: c.goal || '', email_template_slug: c.email_template_slug || '',
                  });
                  setShowForm(true);
                }}><Edit size={14} /></button>
              </div>

              {c.description && <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 8 }}>{c.description}</p>}

              <div style={{ display: 'flex', gap: 16, marginTop: 12, paddingTop: 12, borderTop: '1px solid var(--border)', fontSize: 12 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)' }}>
                  <Users size={12} /> {c.lead_count || 0} Leads
                </div>
                {c.start_date && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)' }}>
                    <Calendar size={12} /> {new Date(c.start_date).toLocaleDateString('de-DE')}
                  </div>
                )}
                {c.goal && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 4, color: 'var(--text-secondary)' }}>
                    <Target size={12} /> {c.goal}
                  </div>
                )}
              </div>
            </div>
          ))
        )}
      </div>

      {/* Campaign Form Modal */}
      {showForm && (
        <div className="modal-overlay" onClick={() => setShowForm(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()} style={{ maxWidth: 560 }}>
            <div className="modal-header">
              <h2>{editing ? 'Kampagne bearbeiten' : 'Neue Kampagne'}</h2>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}><X size={16} /></button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, padding: 20 }}>
              <div style={{ gridColumn: '1/-1' }}>
                <label>Name *</label>
                <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label>Beschreibung</label>
                <textarea rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
              </div>
              <div>
                <label>Kanal</label>
                <select value={form.channel} onChange={e => setForm({ ...form, channel: e.target.value })}>
                  {Object.entries(CHANNEL_MAP).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
              </div>
              <div>
                <label>Status</label>
                <select value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
                  {Object.entries(STATUS_MAP).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div><label>Zielsegment</label><input value={form.target_segment} onChange={e => setForm({ ...form, target_segment: e.target.value })} placeholder="z.B. Fahrschulen >200 Schüler" /></div>
              <div><label>Ziel</label><input value={form.goal} onChange={e => setForm({ ...form, goal: e.target.value })} placeholder="z.B. 50 Demo-Termine" /></div>
              <div><label>Startdatum</label><input type="date" value={form.start_date} onChange={e => setForm({ ...form, start_date: e.target.value })} /></div>
              <div><label>Enddatum</label><input type="date" value={form.end_date} onChange={e => setForm({ ...form, end_date: e.target.value })} /></div>
              <div style={{ gridColumn: '1/-1' }}>
                <label>ETOS Template-Slug</label>
                <input value={form.email_template_slug} onChange={e => setForm({ ...form, email_template_slug: e.target.value })} placeholder="z.B. sales_outreach_v1" />
              </div>
            </div>
            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '12px 20px', borderTop: '1px solid var(--border)' }}>
              <button className="btn btn-ghost" onClick={() => setShowForm(false)}>Abbrechen</button>
              <button className="btn btn-primary" onClick={handleSave} disabled={saving || !form.name}>
                {saving ? 'Speichern...' : editing ? 'Aktualisieren' : 'Erstellen'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
